import { ROUTES } from 'Constants';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TableRow, TableCell } from 'semantic-ui-react';
import { formatStandardDate, formatStandardDateTime } from 'Util/_HumanWritten/DateUtils';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { CustomTooltipWithCustomTrigger } from 'Views/Components/_HumanWritten/CustomTooltip/CustomToolTipWithCustomTrigger';
import { ParkingReservationListDto } from 'Services/Api/_HumanWritten/ParkingService';

interface ParkingReservationRenderProps {
	reservation: ParkingReservationListDto;
	isChecked: boolean;
	onToggleCheckbox: () => void;
	refresh: () => void;
	visibleColumns: Set<string>;
}

/**
 * Derives display info from the status returned by the server.
 * The server computes Active/Completed based on checkedIn + dates.
 */
function getDisplayStatus(status: string): { status: string; text: string; tagClassName: string; tooltip: string } {
	switch (status.toLowerCase()) {
		case 'active':
			return {
				status,
				text: 'Active',
				tagClassName: 'tag--success',
				tooltip: 'Reservation is checked in and currently in the parking period.',
			};
		case 'passed':
			return {
				status,
				text: 'Passed',
				tagClassName: 'grey',
				tooltip: 'Reservation was checked in and has completed the parking period.',
			};
		case 'pending':
			return {
				status,
				text: 'Pending',
				tagClassName: 'tag--warning',
				tooltip: 'Reservation is pending payment confirmation.',
			};
		case 'upcoming':
			return {
				status,
				text: 'Upcoming',
				tagClassName: 'tag--success',
				tooltip: 'Reservation is confirmed and paid.',
			};
		case 'cancelled':
			return { status, text: 'Cancelled', tagClassName: 'tag--error', tooltip: 'Reservation was cancelled.' };
		case 'expired':
			return {
				status,
				text: 'Expired',
				tagClassName: 'grey',
				tooltip: 'Reservation expired without being used.',
			};
		default:
			return { status, text: status, tagClassName: 'grey', tooltip: '' };
	}
}

function getStatusTag(status: string) {
	const display = getDisplayStatus(status);

	return (
		<CustomTooltipWithCustomTrigger
			trigger={<Tag text={display.text} tagClassName={display.tagClassName} />}
			content={display.tooltip}
			toolTipPosition="bottom-left"
		/>
	);
}

export function ParkingReservationRender({
	reservation,
	isChecked,
	onToggleCheckbox,
	refresh,
	visibleColumns,
}: ParkingReservationRenderProps) {
	const show = (key: string) => visibleColumns.has(key);
	const navigate = useNavigate();

	const rowUrl = `/parking-reservations/${reservation.id}`;

	const handleNavigation = (e: React.MouseEvent<HTMLTableRowElement>) => {
		if (e.button === 1 || e.ctrlKey || e.metaKey) {
			window.open(rowUrl, '_blank');
		} else {
			navigate(rowUrl);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			navigate(rowUrl);
		}
	};

	const vehicle = reservation.vehicles?.[0];
	const rego = vehicle?.rego ? vehicle.rego.toUpperCase() : '-';
	const startDate = vehicle ? formatStandardDate(new Date(vehicle.startDate)) : '-';
	const endDate = vehicle ? formatStandardDate(new Date(vehicle.endDate)) : '-';

	return (
		<TableRow
			onClick={handleNavigation}
			onAuxClick={handleNavigation}
			onKeyDown={handleKeyDown}
			className="clickable-row"
			style={{ cursor: 'pointer' }}
			tabIndex={0}
		>
			{show('humanReadableId') && (
				<TableCell>
					<span className="voucher-code">#{reservation.humanReadableId}</span>
				</TableCell>
			)}
			{show('createdDate') && <TableCell>{formatStandardDateTime(new Date(reservation.created))}</TableCell>}
			{show('customer') && (
				<TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
					{reservation.userId ? (
						<Link
							to={ROUTES.USER_DETAIL.url(reservation.userId, 'account')}
							className="customer-details hide-underline"
							style={{ color: 'inherit' }}
						>
							<div className="customer-details__inner">
								<p>{reservation.userName || '-'}</p>
								<p>{reservation.userEmail || ''}</p>
							</div>
						</Link>
					) : (
						'-'
					)}
				</TableCell>
			)}
			{show('location') && <TableCell>{reservation.locationName || '-'}</TableCell>}
			{show('registration') && <TableCell>{rego}</TableCell>}
			{show('startDate') && <TableCell>{startDate}</TableCell>}
			{show('endDate') && <TableCell>{endDate}</TableCell>}
			{show('totalPrice') && <TableCell>${reservation.totalPrice.toFixed(2)}</TableCell>}
			{show('transactionId') && (
				<TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
					{reservation.transactionHumanReadableId ? (
						<Link
							to={`/transactions?search=${encodeURIComponent(reservation.transactionHumanReadableId)}`}
							className="hide-underline"
							style={{ color: 'inherit' }}
						>
							#{reservation.transactionHumanReadableId}
						</Link>
					) : (
						'-'
					)}
				</TableCell>
			)}
			{show('status') && <TableCell>{getStatusTag(reservation.status)}</TableCell>}
		</TableRow>
	);
}
